import { useState } from "react";
import { getHistoryCount } from "@/lib/history";
import TrendingAddBanner from "@/components/TrendingAddBanner";
import ResultRow from "@/components/search/ResultRow";

// One config entry per spectrum column — drives both the mobile toggle
// and the desktop three-column layout.
const COLUMNS = [
  {
    id: "dark",
    label: "Dark",
    symbol: "⛧",
    variant: "dark",
    pool: "results_dark",
    totalKey: "total_dark",
    keyPrefix: "dk-",
    listTestId: "dark-narrative-list",
    titleStyle: { color: "#F87171" },
    titleClass: "",
    symbolClass: "",
    lineClass: "bg-gradient-to-r from-[rgba(239,68,68,0.5)] to-transparent",
    emptyPanelClass: "panel panel-dark",
    emptyText: "No dark-coded matches",
    activeToggleClass: "bg-[rgba(239,68,68,0.18)]",
    activeToggleStyle: { color: "#F87171" },
  },
  {
    id: "neutral",
    label: "Neutral",
    symbol: "○",
    variant: undefined,
    pool: "results_neutral",
    totalKey: "total_neutral",
    keyPrefix: "nu-",
    listTestId: "neutral-list",
    titleStyle: undefined,
    titleClass: "text-white",
    symbolClass: "muted",
    lineClass: "bg-gradient-to-r from-white/25 to-transparent",
    emptyPanelClass: "panel",
    emptyText: "No neutral matches",
    activeToggleClass: "bg-white/10 text-white",
    activeToggleStyle: undefined,
  },
  {
    id: "light",
    label: "Light",
    symbol: "✦",
    variant: "light",
    pool: "results_light",
    totalKey: "total_light",
    keyPrefix: "lt-",
    listTestId: "light-narrative-list",
    titleStyle: { color: "#6EE7B7" },
    titleClass: "",
    symbolClass: "",
    lineClass: "bg-gradient-to-r from-[rgba(16,185,129,0.5)] to-transparent",
    emptyPanelClass: "panel panel-light",
    emptyText: "No light-coded matches",
    activeToggleClass: "bg-[rgba(16,185,129,0.18)]",
    activeToggleStyle: { color: "#6EE7B7" },
  },
];

export default function ResultsView({ results, cipherLabel, onResearch }) {
  const [mobilePane, setMobilePane] = useState("dark");

  return (
    <div className="rise rise-2 space-y-6">
      {getHistoryCount(results.value) >= 3 && (
        <TrendingAddBanner
          targetValue={results.value}
          searchCount={getHistoryCount(results.value)}
          onAdded={() => onResearch(String(results.value))}
        />
      )}

      <div className="flex items-baseline justify-between mb-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] muted">
            Matches for
          </div>
          <div className="font-display text-5xl big-number leading-none mt-1">
            = {results.value}
          </div>
        </div>
        <div className="font-mono text-xs muted text-right leading-relaxed">
          {results.total} phrase{results.total === 1 ? "" : "s"} found
          <div className="flex flex-col items-end gap-0.5 mt-1">
            <div className="text-[10px] tracking-wider" style={{ color: "#F87171" }}>
              ⛧ {results.total_dark || 0} dark
            </div>
            <div className="text-[10px] tracking-wider muted">
              ○ {results.total_neutral || 0} neutral
            </div>
            <div className="text-[10px] tracking-wider" style={{ color: "#6EE7B7" }}>
              ✦ {results.total_light || 0} light
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE TOGGLE — Dark / Neutral / Light spectrum */}
      <div className="lg:hidden flex gap-1 p-1 bg-white/[0.03] border border-white/10 rounded-sm">
        {COLUMNS.map((col) => {
          const active = mobilePane === col.id;
          return (
            <button
              key={col.id}
              data-testid={`mobile-toggle-${col.id}`}
              onClick={() => setMobilePane(col.id)}
              className={`flex-1 py-2.5 text-[11px] font-mono uppercase tracking-[0.12em] transition-colors flex items-center justify-center gap-1.5 ${
                active ? col.activeToggleClass : "muted hover:text-white"
              }`}
              style={active ? col.activeToggleStyle : undefined}
            >
              <span aria-hidden="true">{col.symbol}</span>
              {col.label} · {results[col.totalKey] || 0}
            </button>
          );
        })}
      </div>

      {/* THREE-COLUMN SPECTRUM (desktop) / TOGGLED SINGLE-PANE (mobile) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {COLUMNS.map((col) => {
          const items = results[col.pool] || [];
          return (
            <div
              key={col.id}
              className={`space-y-3 ${mobilePane === col.id ? "" : "hidden lg:block"}`}
              data-testid={col.listTestId}
            >
              <div className="hidden lg:flex items-center gap-3 pb-1 sticky top-[88px] z-10 bg-[#0a0a0c]/85 backdrop-blur-sm py-2 -mx-1 px-1">
                <span className={`text-lg select-none ${col.symbolClass}`} aria-hidden="true">{col.symbol}</span>
                <h3
                  className={`font-display text-xl tracking-tight whitespace-nowrap ${col.titleClass}`}
                  style={col.titleStyle}
                >
                  {col.label}
                </h3>
                <div className={`flex-1 h-px ${col.lineClass}`} />
              </div>
              {items.length === 0 ? (
                <div className={`${col.emptyPanelClass} p-8 text-center muted font-mono text-sm`}>
                  {col.emptyText} for {results.value}.
                </div>
              ) : (
                items.map((r, i) => (
                  <ResultRow
                    key={col.keyPrefix + r.id}
                    item={r}
                    cipherLabel={cipherLabel}
                    idx={i}
                    variant={col.variant}
                    onReclassified={() => onResearch(String(results.value))}
                  />
                ))
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
